import { ProductCategory } from "@/constant/common";

// Single source of truth for the products marketed on the home page.
// The Products grid and the Pricing section both read from here, so they can
// never disagree on which products exist or each product's billing model (R10).
// Marketing copy is transcribed from the design mockup (Instaticker Homepage).

export type HomeBilling = "payg" | "subscription";

export interface HomeProduct {
  name: string;
  blurb: string;
  bullets: string[];
  billing: HomeBilling;
}

// A Record over ProductCategory forces an entry for every product at compile
// time — omitting one fails `tsc`. `billing` mirrors the price constants in
// constant/common.ts: PRICE_PER_* products are "payg"; *_MONTHLY_USD products
// are "subscription".
export const HOME_PRODUCTS_BY_KEY: Record<ProductCategory, HomeProduct> = {
  [ProductCategory.SMS]: {
    name: "SMS API",
    blurb: "Instant US Toll-Free number with multi-provider routing.",
    bullets: [
      "AI compliance scanning",
      "Multi-provider routing",
      "Real-time delivery logs",
    ],
    billing: "payg", // cf. PRICE_PER_SMS
  },
  [ProductCategory.EMAIL]: {
    name: "Email API",
    blurb: "Send transactional email with full delivery logs.",
    bullets: [
      "Transactional delivery",
      "Open & delivery logs",
      "Webhook events",
    ],
    billing: "subscription", // cf. EMAIL_MONTHLY_USD
  },
  [ProductCategory.ADDRESS]: {
    name: "Address Validation API",
    blurb: "Validate and standardize postal addresses via API.",
    bullets: [
      "Real-time verification",
      "Standardized output",
      "Flat $99/month, unlimited validations",
    ],
    billing: "subscription", // cf. ADDRESS_MONTHLY_USD
  },
  [ProductCategory.FAX]: {
    name: "Fax API",
    blurb: "Send fax programmatically with delivery tracking.",
    bullets: [
      "PDF-to-fax in one call",
      "Delivery tracking",
      "Status webhooks",
    ],
    billing: "payg", // cf. PRICE_PER_FAX
  },
  [ProductCategory.IP2GEO]: {
    name: "ip2geo API",
    blurb: "IP-to-geolocation lookups with domain management.",
    bullets: [
      "City-level accuracy",
      "Domain management",
      "High request volume",
    ],
    billing: "subscription", // cf. IP2GEO_MONTHLY_USD
  },
  [ProductCategory.ANALYTICS]: {
    name: "Web Analytics",
    blurb: "Privacy-friendly, self-hosted analytics you control.",
    bullets: [
      "Self-hosted & private",
      "Selectable AWS region",
      "Pick your instance size",
    ],
    billing: "subscription", // cf. ANALYTICS_MONTHLY_USD
  },
  [ProductCategory.EMAIL_CAMPAIGN]: {
    name: "Email Campaigns",
    blurb: "Import a list, design an email, and send it — from your own AWS account.",
    bullets: [
      "1¢ per email, no monthly fee",
      "Sends from your own AWS account",
      "Visual editor & DKIM senders",
    ],
    billing: "payg", // cf. PRICE_PER_EMAIL_CAMPAIGN
  },
};

// Per-section display order. The mockup orders ip2geo and Web Analytics
// differently in the Products grid vs the Pricing subscription column, so each
// section keeps its own order array (all drawn from the same product set).
export const PRODUCT_GRID_ORDER: ProductCategory[] = [
  ProductCategory.SMS,
  ProductCategory.EMAIL,
  ProductCategory.ADDRESS,
  ProductCategory.FAX,
  ProductCategory.IP2GEO,
  ProductCategory.ANALYTICS,
  ProductCategory.EMAIL_CAMPAIGN,
];

export const PRICING_PAYG_ORDER: ProductCategory[] = [
  ProductCategory.SMS,
  ProductCategory.FAX,
  ProductCategory.EMAIL_CAMPAIGN,
];

export const PRICING_SUBSCRIPTION_ORDER: ProductCategory[] = [
  ProductCategory.EMAIL,
  ProductCategory.ANALYTICS,
  ProductCategory.IP2GEO,
  ProductCategory.ADDRESS,
];

export const BILLING_BADGE_LABEL: Record<HomeBilling, string> = {
  payg: "Pay as you go",
  subscription: "Monthly subscription",
};

// Dev-only integrity guard: ensures the display-order arrays stay a complete,
// non-duplicated partition of the real product set, and that each pricing column
// matches its products' billing model. No-op in production.
if (process.env.NODE_ENV !== "production") {
  const allKeys = Object.keys(HOME_PRODUCTS_BY_KEY) as ProductCategory[];

  const assertPermutation = (label: string, arr: ProductCategory[]) => {
    const unique = new Set(arr);
    if (unique.size !== arr.length) {
      console.error(`[productData] ${label} contains duplicates`);
    }
    if (arr.length !== allKeys.length || allKeys.some((k) => !unique.has(k))) {
      console.error(`[productData] ${label} is not a permutation of all products`);
    }
  };

  assertPermutation("PRODUCT_GRID_ORDER", PRODUCT_GRID_ORDER);

  const pricingUnion = [...PRICING_PAYG_ORDER, ...PRICING_SUBSCRIPTION_ORDER];
  assertPermutation("PRICING_PAYG_ORDER ∪ PRICING_SUBSCRIPTION_ORDER", pricingUnion);

  PRICING_PAYG_ORDER.forEach((k) => {
    if (HOME_PRODUCTS_BY_KEY[k].billing !== "payg") {
      console.error(`[productData] ${k} is in the PAYG column but not billed payg`);
    }
  });
  PRICING_SUBSCRIPTION_ORDER.forEach((k) => {
    if (HOME_PRODUCTS_BY_KEY[k].billing !== "subscription") {
      console.error(`[productData] ${k} is in the subscription column but not billed subscription`);
    }
  });
}
